/**
 * EE API Keys 管理頁（Enterprise Edition only）
 *
 * Tab 1 憑證清單：CRUD + 撤銷 + 輪替
 * Tab 2 稽核紀錄：全域分頁列表
 *
 * 簽發 / 輪替成功時跳出「一次性明文」Modal（紅色警示 + 複製按鈕，
 * 關閉後無法再取得）。
 *
 * 對應後端：/api/ee/api_keys/*
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Tabs, Tab, Box, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, FormControlLabel, Switch, Select, MenuItem, InputLabel,
  FormControl, IconButton, Chip, Table, TableBody, TableCell,
  TableHead, TableRow, TableContainer, Paper, Tooltip, Typography,
  Stack, Divider, Pagination, Alert, OutlinedInput,
} from '@mui/material';
import {
  Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon,
  Refresh as RefreshIcon, Cached as RotateIcon, ContentCopy as CopyIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

import FunctionPageHeader from '../../components/FunctionPageHeader';
import { useMessage } from '../../contexts/MessageContext';
import { getI18nValue } from '../../utils/i18nHelper';
import { useTranslation } from 'react-i18next';
import { ApiRole, listApiRoles } from '../../services/eeApiRolesService';
import {
  ApiKey, ApiKeyCreate, ApiKeyUpdate, ApiKeyAudit,
  listApiKeys, issueApiKey, updateApiKey, revokeApiKey, rotateApiKey,
  listAllAudit, listKeyAudit,
} from '../../services/eeApiKeysService';
import { formatLocal } from '../../utils/dateHelper';


const formatDateTime = (iso?: string | null): string => {
  if (!iso) return '—';
  try { return formatLocal(iso); } catch { return iso; }
};

const copyToClipboard = async (text: string) => {
  try { await navigator.clipboard.writeText(text); return true; }
  catch { return false; }
};


// ============================================================
// 一次性明文 Modal（簽發/輪替時顯示）
// ============================================================

const PlaintextModal: React.FC<{
  open: boolean;
  plaintext: string;
  keyName: string;
  onClose: () => void;
}> = ({ open, plaintext, keyName, onClose }) => {
  const { showSuccess } = useMessage();
  const [copied, setCopied] = useState(false);

  return (
    <Dialog open={open} onClose={() => {}} fullWidth maxWidth="md" disableEscapeKeyDown>
      <DialogTitle sx={{ bgcolor: 'error.main', color: 'error.contrastText' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <WarningIcon />
          <span>Key 僅顯示一次，請立即妥善保存</span>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          此 API Key 只會出現這一次。關閉本視窗後即無法再取得，僅能另行輪替。
        </Alert>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>名稱：</strong>{keyName}
        </Typography>
        <Paper variant="outlined" sx={{
          p: 2, bgcolor: 'grey.100',
          fontFamily: 'monospace', fontSize: '1.1em',
          wordBreak: 'break-all', mb: 1,
        }}>
          {plaintext}
        </Paper>
        <Button size="small" startIcon={<CopyIcon />}
                onClick={async () => {
                  const ok = await copyToClipboard(plaintext);
                  if (ok) { showSuccess('SYS020002', { operation: '複製' }); setCopied(true); }
                }}>
          {copied ? '已複製' : '複製到剪貼簿'}
        </Button>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="error" onClick={onClose}
                disabled={!copied}
                title={!copied ? '請先複製後才能關閉' : ''}>
          我已妥善保存
        </Button>
      </DialogActions>
    </Dialog>
  );
};


// ============================================================
// Keys Tab
// ============================================================

const KeysTab: React.FC<{ roles: ApiRole[]; reloadRoles: () => void }> = ({ roles, reloadRoles }) => {
  const { i18n } = useTranslation();
  const { showSuccess, showApiError } = useMessage();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<ApiKey | null>(null);
  const [form, setForm] = useState<ApiKeyCreate>({
    key_name: '', api_role: [], description: '', expires_days: 365,
  });

  const [plaintextOpen, setPlaintextOpen] = useState(false);
  const [plaintextValue, setPlaintextValue] = useState('');
  const [plaintextKeyName, setPlaintextKeyName] = useState('');

  const reload = useCallback(async () => {
    setLoading(true);
    try { setKeys(await listApiKeys()); }
    catch (err) { showApiError(err); }
    finally { setLoading(false); }
  }, [showApiError]);

  useEffect(() => { reload(); }, [reload]);

  const openCreate = () => {
    setEditing(null);
    setForm({ key_name: '', api_role: [], description: '', expires_days: 365 });
    setEditOpen(true);
  };

  const openEdit = (k: ApiKey) => {
    setEditing(k);
    setForm({
      key_name: k.key_name,
      api_role: k.api_role || [],
      description: k.description || '',
      expires_days: null,
      expires_at: k.expires_at,
    });
    setEditOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        const payload: ApiKeyUpdate = {
          key_name: form.key_name,
          api_role: form.api_role,
          description: form.description,
          expires_at: form.expires_at,
        };
        await updateApiKey(editing.id, payload);
        showSuccess('SYS040008', { name: form.key_name });
        setEditOpen(false);
        reload();
      } else {
        const res = await issueApiKey(form);
        showSuccess('SYS040005', { name: form.key_name });
        setEditOpen(false);
        setPlaintextValue(res.plaintext_key);
        setPlaintextKeyName(res.api_key.key_name);
        setPlaintextOpen(true);
        reload();
      }
    } catch (err) { showApiError(err); }
  };

  const handleRevoke = async (k: ApiKey) => {
    const reason = window.prompt(`撤銷 Key「${k.key_name}」的原因？（可留空）`, '');
    if (reason === null) return;
    try {
      await revokeApiKey(k.id, reason || null);
      showSuccess('SYS040006', { name: k.key_name });
      reload();
    } catch (err) { showApiError(err); }
  };

  const handleRotate = async (k: ApiKey) => {
    const reason = window.prompt(`輪替 Key「${k.key_name}」？舊 Key 會立即撤銷、新 Key 繼承設定。原因（可留空）：`, '');
    if (reason === null) return;
    try {
      const res = await rotateApiKey(k.id, reason || null);
      showSuccess('SYS040007', { name: k.key_name });
      setPlaintextValue(res.plaintext_key);
      setPlaintextKeyName(res.new_api_key.key_name);
      setPlaintextOpen(true);
      reload();
    } catch (err) { showApiError(err); }
  };

  const roleChip = (roleId: number) => {
    const r = roles.find(x => x.id === roleId);
    const label = r ? (getI18nValue(r.role_name, i18n.language) || r.role_code) : `#${roleId}`;
    return <Chip key={roleId} label={label} size="small" variant="outlined" sx={{ mr: 0.5 }} />;
  };

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ my: 2 }}>
        <Button variant="contained" size="small" startIcon={<AddIcon />}
                onClick={openCreate} disabled={roles.length === 0}>
          簽發 Key
        </Button>
        <Button variant="outlined" size="small" startIcon={<RefreshIcon />}
                onClick={() => { reload(); reloadRoles(); }} disabled={loading}>
          重整
        </Button>
        <Typography variant="body2" sx={{ ml: 'auto', alignSelf: 'center', color: 'text.secondary' }}>
          共 {keys.length} 把 Key
        </Typography>
      </Stack>

      {roles.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          尚無 API 角色。請先到「API 角色管理」建立角色，才能簽發 Key。
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>名稱</TableCell>
              <TableCell>前綴</TableCell>
              <TableCell>角色</TableCell>
              <TableCell align="center">啟用</TableCell>
              <TableCell>簽發 / 過期</TableCell>
              <TableCell>最近使用</TableCell>
              <TableCell align="right">累計</TableCell>
              <TableCell align="right">動作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {keys.map(k => (
              <TableRow key={k.id} hover>
                <TableCell>{k.id}</TableCell>
                <TableCell>{k.key_name}</TableCell>
                <TableCell><code style={{ fontSize: '0.85em' }}>{k.key_prefix}</code></TableCell>
                <TableCell>{(k.api_role || []).map(roleChip)}</TableCell>
                <TableCell align="center">
                  <Chip label={k.is_active ? '啟用' : (k.revoked_at ? '撤銷' : '停用')} size="small"
                        color={k.is_active ? 'success' : 'default'} />
                </TableCell>
                <TableCell>
                  <div style={{ fontSize: '0.8em' }}>簽 {formatDateTime(k.issued_at)}</div>
                  <div style={{ fontSize: '0.8em', color: k.expires_at ? 'inherit' : '#888' }}>
                    {k.expires_at ? `至 ${formatDateTime(k.expires_at)}` : '永不過期'}
                  </div>
                </TableCell>
                <TableCell sx={{ fontSize: '0.85em' }}>
                  {k.last_used_at
                    ? <>{formatDateTime(k.last_used_at)}<br />{k.last_used_ip}</>
                    : '—'}
                </TableCell>
                <TableCell align="right">{k.use_count}</TableCell>
                <TableCell align="right">
                  <Tooltip title="編輯">
                    <IconButton size="small" onClick={() => openEdit(k)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="輪替">
                    <span>
                      <IconButton size="small" onClick={() => handleRotate(k)}
                                  disabled={!!k.revoked_at}>
                        <RotateIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="撤銷">
                    <span>
                      <IconButton size="small" onClick={() => handleRevoke(k)}
                                  disabled={!!k.revoked_at}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {keys.length === 0 && (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                {loading ? '載入中…' : '（尚無 Key）'}
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 編輯 / 簽發 Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? '編輯 API Key' : '簽發新 API Key'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="名稱" size="small" fullWidth required
                       value={form.key_name}
                       onChange={e => setForm({ ...form, key_name: e.target.value })} />
            <FormControl size="small" fullWidth>
              <InputLabel>角色（多選）</InputLabel>
              <Select multiple value={form.api_role} label="角色（多選）"
                      input={<OutlinedInput label="角色（多選）" />}
                      onChange={e => {
                        const val = e.target.value;
                        setForm({ ...form, api_role: Array.isArray(val) ? (val as number[]) : [] });
                      }}
                      renderValue={(selected) => (selected as number[]).map(id => {
                        const r = roles.find(x => x.id === id);
                        return <Chip key={id} label={r ? r.role_code : `#${id}`}
                                     size="small" sx={{ mr: 0.5 }} />;
                      })}>
                {roles.filter(r => r.is_active).map(r => (
                  <MenuItem key={r.id} value={r.id}>
                    <code>{r.role_code}</code>
                    <span style={{ marginLeft: 8, color: '#888' }}>
                      {getI18nValue(r.role_name, i18n.language) || ''}
                    </span>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {!editing && (
              <TextField label="有效天數（0 或留空 = 永不過期）" size="small" fullWidth type="number"
                         inputProps={{ min: 0 }} value={form.expires_days ?? ''}
                         onChange={e => setForm({
                           ...form,
                           expires_days: e.target.value === '' ? null : Number(e.target.value),
                         })} />
            )}
            {editing && (
              <TextField label="過期時間（ISO / 留空=永不）" size="small" fullWidth
                         value={form.expires_at || ''}
                         onChange={e => setForm({ ...form, expires_at: e.target.value || null })}
                         helperText="ex. 2027-04-20T00:00:00Z；留空表示永不過期" />
            )}
            <TextField label="說明" size="small" fullWidth multiline minRows={2}
                       value={form.description || ''}
                       onChange={e => setForm({ ...form, description: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>取消</Button>
          <Button variant="contained" onClick={handleSave}
                  disabled={!form.key_name || form.api_role.length === 0}>
            {editing ? '儲存' : '簽發'}
          </Button>
        </DialogActions>
      </Dialog>

      <PlaintextModal open={plaintextOpen} plaintext={plaintextValue}
                      keyName={plaintextKeyName}
                      onClose={() => { setPlaintextOpen(false); setPlaintextValue(''); }} />
    </>
  );
};


// ============================================================
// Audit Tab
// ============================================================

const AuditTab: React.FC = () => {
  const { showApiError } = useMessage();
  const [rows, setRows] = useState<ApiKeyAudit[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [funcFilter, setFuncFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const LIMIT = 20;

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { offset: (page - 1) * LIMIT, limit: LIMIT };
      if (funcFilter) params.func_code = funcFilter;
      if (statusFilter) params.status_code = Number(statusFilter);
      const res = await listAllAudit(params);
      setRows(res.items);
      setTotal(res.total);
    } catch (err) { showApiError(err); }
    finally { setLoading(false); }
  }, [page, funcFilter, statusFilter, showApiError]);

  useEffect(() => { reload(); }, [reload]);

  const statusColor = (code: number) => code < 300 ? 'success' : code < 400 ? 'info' : 'error';

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <TextField label="func_code" size="small" value={funcFilter}
                   onChange={e => { setFuncFilter(e.target.value); setPage(1); }} />
        <TextField label="status_code" size="small" type="number" value={statusFilter}
                   onChange={e => { setStatusFilter(e.target.value); setPage(1); }} />
        <Button size="small" startIcon={<RefreshIcon />} onClick={reload} disabled={loading}>
          重整
        </Button>
        <Typography variant="body2" sx={{ ml: 'auto', alignSelf: 'center', color: 'text.secondary' }}>
          共 {total} 筆
        </Typography>
      </Stack>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>時間</TableCell>
              <TableCell>Key</TableCell>
              <TableCell>服務</TableCell>
              <TableCell>Method</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="right">耗時(ms)</TableCell>
              <TableCell>IP</TableCell>
              <TableCell>Error</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.id} hover>
                <TableCell>{r.id}</TableCell>
                <TableCell sx={{ fontSize: '0.8em' }}>{formatDateTime(r.started_at)}</TableCell>
                <TableCell><code style={{ fontSize: '0.8em' }}>{r.key_prefix}</code></TableCell>
                <TableCell><code>{r.api_func_code}</code></TableCell>
                <TableCell>{r.method}</TableCell>
                <TableCell align="center">
                  <Chip label={r.status_code} size="small" color={statusColor(r.status_code) as any} />
                </TableCell>
                <TableCell align="right">{r.duration_ms ?? '—'}</TableCell>
                <TableCell sx={{ fontSize: '0.85em' }}>{r.client_ip || '—'}</TableCell>
                <TableCell sx={{ fontSize: '0.85em', color: 'error.main' }}>
                  {r.error_code || ''}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                {loading ? '載入中…' : '（無稽核紀錄）'}
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <Pagination count={Math.max(1, Math.ceil(total / LIMIT))}
                    page={page} onChange={(_, v) => setPage(v)} />
      </Box>
    </Box>
  );
};


// ============================================================
// Main
// ============================================================

const EeApiKeysPage: React.FC = () => {
  const { showApiError } = useMessage();
  const [tab, setTab] = useState(0);
  const [roles, setRoles] = useState<ApiRole[]>([]);

  const reloadRoles = useCallback(async () => {
    try { setRoles(await listApiRoles()); }
    catch (err) { showApiError(err); }
  }, [showApiError]);

  useEffect(() => { reloadRoles(); }, [reloadRoles]);

  return (
    <Box>
      <FunctionPageHeader funcCode="ee_api_keys" fallback="API 憑證管理" />
      <Tabs value={tab} onChange={(_, v) => setTab(v)}
            sx={{ mt: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="憑證清單" />
        <Tab label="稽核紀錄" />
      </Tabs>
      <Box sx={{ mt: 1 }}>
        {tab === 0 && <KeysTab roles={roles} reloadRoles={reloadRoles} />}
        {tab === 1 && <AuditTab />}
      </Box>
    </Box>
  );
};

export default EeApiKeysPage;